import React, { forwardRef, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
    ChevronDown,
    ChevronRight,
    ChevronUp,
    ChevronLeft,
} from "lucide-react";
import { Calendar } from "primereact/calendar";
import { MyPanelModal } from "../../pages/MeuPainel/modal";
import { CartoesDTO } from "./IExtratoMensal";
import {
    processarCartoesEspeciais,
    processarCartoesJustificados,
} from "./ProcessarCartoesJustificados";

interface ExtratoData {
    naoAlocados: { naoLetivo: number; especiais: number };
    justificados: { grupo1: number; grupo2: number };
    especiais: { antecipacao: number; reposicao: number; substituicao: number };
    substituidas: { deferida: number; indeferida: number };
    letivaRE: {
        planejadas: number;
        realizadas: number;
        naoRealizadas: number;
        naoAlocadas: number;
    };
}

type SectionKey =
    | "naoAlocados"
    | "justificados"
    | "especiais"
    | "substituidas"
    | "letivaRE";

const Wrapper = styled.div`
    min-height: 100vh;
    padding: 0px 1rem;
`;

const Container = styled.div`
    max-width: 800px;
    margin: 0 auto;
`;

const Title = styled.h2`
    font-family: "Poppins";
    font-weight: 500;
    font-style: normal;
    font-size: 24px;
    line-height: 140%;
    letter-spacing: 0px;
    vertical-align: middle;
    color: #585858;
`;

const Description = styled.p`
    font-family: "Source Sans Pro";
    font-weight: 400;
    font-style: Regular;
    font-size: 17px;
    line-height: 156%;
    letter-spacing: 0px;
    vertical-align: middle;
    color: #383838;
`;

const Attention = styled.p`
    font-family: "Source Sans Pro";
    font-weight: 600;
    font-style: normal;
    font-size: 15px;
    line-height: 156%;
    letter-spacing: 0px;
    color: #863bff;
`;

const MonthSelector = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 0.75rem 1.5rem;
    gap: 1.25rem;
    font-weight: 500;
    color: #374151;
    box-shadow: rgba(0, 0, 0, 0.04) 0px 2px 4px;
`;
const MonthButton = styled.button`
    background: none;
    border: none;
    font-size: 1.25rem;
    padding: 0.25rem 0.5rem;
    cursor: pointer;
    color: rgb(138, 5, 56);
    display: flex;
    align-items: center;
`;

const AccordionWrapper = styled.div`
    border-radius: 18px;
    border: 1px solid #0000001a;
    box-shadow: rgba(0, 0, 0, 0.12) 0px 4px 24px 0px;
    background: rgb(255, 255, 255);
    margin-bottom: 1rem;
`;

const AccordionHeader = styled.button`
    width: 100%;
    padding: 1rem;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    align-items: center;
    background: white;
    border: none;
    border-bottom: 1px solid #f3f4f6;
    cursor: pointer;
    font-size: 1rem;
    font-weight: 600;
    color: #1f2937;
    border-radius: 24px;

    .titulo {
        text-align: left;
    }

    .valor {
        font-variant-numeric: tabular-nums;
        color: #111827;
        font-weight: bold;
        text-align: left;
    }

    .icone {
        justify-self: right;
    }
    &.accordion-header {
        cursor: pointer;
    }
`;

const AccordionContent = styled.div`
    background-color: #f9fafb;
    padding: 1rem;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 1rem;
    border-radius: 24px;
`;

const Card = styled.div`
    background: white;
    border-radius: 0.75rem;
    padding: 1rem;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    cursor: pointer;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    border: 1px solid #e5e7eb;
`;

const CardTitle = styled.div`
    font-size: 0.9rem;
    color: #4b5563;
`;

const CardValue = styled.div`
    font-size: 1.5rem;
    font-weight: bold;
    color: #111827;
`;

const CardContent = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
`;

const MonthSection = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
    margin: 1.5rem 0;
    flex-wrap: wrap;

    .month-title {
        fontFamily: "Source Sans Pro",
        fontWeight: 600,
        fontStyle: "normal",
        fontSize: "17px",
        lineHeight: "150%",
        letterSpacing: "0px",
        color: #404040;
    }
`;

const CaixaResumo = styled.div`
    background-color: #f0f2f2;
    border-radius: 16px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const CardWithArrow = forwardRef(
    (
        {
            titulo,
            valor,
            onClick,
        }: {
            titulo: string;
            valor: string;
            onClick?: () => void;
        },
        ref: React.Ref<HTMLDivElement>
    ) => (
        <Card
            ref={ref}
            className="card-with-arrow"
            onClick={(event) => {
                event.stopPropagation();
                onClick?.();
            }}
            style={{
                minHeight: "50px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
            }}
        >
            <CardContent style={{ flexGrow: 1 }}>
                <CardTitle>{titulo}</CardTitle>
            </CardContent>
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "0 1rem 0.5rem",
                }}
            >
                <CardValue>{valor}</CardValue>
                <ChevronRight
                    size={20}
                    color="rgb(138, 5, 56)"
                    strokeWidth={2.5}
                />
            </div>
        </Card>
    )
);

const buscarExtratoPorMes = async (
    mes: number,
    _ano: number
): Promise<ExtratoData> => {
    await new Promise<void>((resolve) => {
        setTimeout(resolve, 200);
    });
    const base = mes + 1;
    return {
        naoAlocados: { naoLetivo: base * 2, especiais: base },
        justificados: { grupo1: base % 3, grupo2: base + 5 },
        especiais: {
            antecipacao: base,
            reposicao: base + 2,
            substituicao: base + 1,
        },
        substituidas: {
            deferida: base + 3,
            indeferida: base % 5,
        },
        letivaRE: {
            planejadas: base,
            realizadas: base - 1,
            naoRealizadas: 1,
            naoAlocadas: 2,
        },
    };
};

interface ExtratoMensalProps {
    extrato: CartoesDTO | null;
    onChangeMes: (mes: number, ano: number) => void;
}

interface Atividade {
    atividade: string;
    curso: string;
    tipoCartao: string;
    tipoAtividade: string;
    cargaHoraria: string;
    motivoJustificativa: string;
    dataAlocacao: string;
    __periodoReferenciaCustom: string;
    docenteSubstituto: string;
    statusCheckin: string;
    cartaoVinculado: string;
    docenteSubstituido: string;
}

const ExtratoMensal: React.FC<ExtratoMensalProps> = ({
    extrato,
    onChangeMes,
    // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
    const [mesSelecionado, setMesSelecionado] = useState(new Date());
    const [dados, setDados] = useState<ExtratoData | null>(null);
    const [sectionsOpen, setSectionsOpen] = useState<
        Record<SectionKey, boolean>
    >({
        naoAlocados: false,
        justificados: false,
        especiais: false,
        substituidas: false,
        letivaRE: false,
    });
    const [mostrarCalendario, setMostrarCalendario] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const refCalendario = useRef<HTMLDivElement>(null);

    const [showDialogNaoAlocados, setShowDialogNaoAlocados] = useState(false);
    const [showDialogNaoAlocadosEspeciais, setShowDialogNaoAlocadosEspeciais] =
        useState(false);
    const [showDialogCartoesJustificados, setShowDialogCartoesJustificados] =
        useState(false);
    const [
        showDialogCartoesJustificadosImpactaFechamento,
        setShowDialogCartoesJustificadosImpactaFechamento,
    ] = useState(false);
    const [showDialogDeferida, setShowDialogDeferida] = useState(false);
    const [showDialogIndeferida, setShowDialogIndeferida] = useState(false);
    const [showDialogAntecipacao, setShowDialogAntecipacao] = useState(false);
    const [showDialogReposicao, setShowDialogReposicao] = useState(false);
    const [showDialogSubstituicaoCriados, setShowDialogSubstituicaoCriados] =
        useState(false);

    const [showDialogPlanejadas, setshowDialogPlanejadas] = useState(false);
    const [showDialogRealizadas, setshowDialogRealizadas] = useState(false);
    const [showDialogNaoAlocadas, setshowDialogNaoAlocadas] = useState(false);
    const [showDialogNaoRealizadas, setshowDialogNaoRealizadas] =
        useState(false);
    const COLUNA_ATIVIDADE = "Atividade";
    const COLUNA_CURSO = "Curso";
    const COLUNA_TIPO_CARTAO = "Tipo de Cartão";
    const COLUNA_TIPO_ATIVIDADE = "Tipo de Atividade";
    const COLUNA_CARGA_HORARIA = "CH (hora/aula)";
    const COLUNA_PERIODO_REFENCIA = "Período de referência";
    const COLUNA_CARTAO_VINCULADO = "Cartão vinculado Atividade original";
    const COLUNA_DATA_DE_ALOCACAO = "Data de alocação";

    const COLUNAS_MODAL_NAO_ALOCADOS = [
        COLUNA_ATIVIDADE,
        COLUNA_CURSO,
        COLUNA_TIPO_CARTAO,
        COLUNA_TIPO_ATIVIDADE,
        COLUNA_CARGA_HORARIA,
        COLUNA_PERIODO_REFENCIA,
    ];

    const COLUNAS_MODAL_NAO_ALOCADOS_ESPECIAIS = [
        COLUNA_ATIVIDADE,
        COLUNA_CURSO,
        COLUNA_TIPO_CARTAO,
        COLUNA_TIPO_ATIVIDADE,
        COLUNA_CARGA_HORARIA,
        COLUNA_PERIODO_REFENCIA,
    ];

    const COLUNAS_MODAL_CARTOES_JUSTIFICADOS = [
        COLUNA_ATIVIDADE,
        COLUNA_CURSO,
        COLUNA_TIPO_CARTAO,
        COLUNA_TIPO_ATIVIDADE,
        "Motivo justificativa",
        COLUNA_PERIODO_REFENCIA,
    ];

    const COLUNAS_MODAL_CARTOES_JUSTIFICADOS_IMPACTA_FECHAMENTO = [
        COLUNA_ATIVIDADE,
        COLUNA_CURSO,
        COLUNA_TIPO_CARTAO,
        COLUNA_TIPO_ATIVIDADE,
        "Motivo justificativa",
        COLUNA_PERIODO_REFENCIA,
    ];

    const COLUNAS_MODAL_DEFERIDA = [
        COLUNA_ATIVIDADE,
        COLUNA_CURSO,
        COLUNA_TIPO_CARTAO,
        COLUNA_TIPO_ATIVIDADE,
        COLUNA_DATA_DE_ALOCACAO,
        "Status",
        "Docente substituto",
    ];

    const COLUNAS_MODAL_INDEFERIDA = [
        COLUNA_ATIVIDADE,
        COLUNA_CURSO,
        COLUNA_TIPO_CARTAO,
        COLUNA_TIPO_ATIVIDADE,
        COLUNA_DATA_DE_ALOCACAO,
        "Status",
        "Docente substituto",
    ];

    const COLUNAS_MODAL_ANTECIPACAO = [
        COLUNA_ATIVIDADE,
        COLUNA_CURSO,
        COLUNA_TIPO_CARTAO,
        COLUNA_TIPO_ATIVIDADE,
        COLUNA_CARGA_HORARIA,
        COLUNA_PERIODO_REFENCIA,
        COLUNA_CARTAO_VINCULADO,
    ];

    const COLUNAS_MODAL_REPOSICAO = [
        COLUNA_ATIVIDADE,
        COLUNA_CURSO,
        COLUNA_TIPO_CARTAO,
        COLUNA_TIPO_ATIVIDADE,
        COLUNA_CARGA_HORARIA,
        COLUNA_PERIODO_REFENCIA,
    ];

    const COLUNAS_MODAL_SUBSTITUICAO_CRIADOS = [
        COLUNA_ATIVIDADE,
        COLUNA_CURSO,
        COLUNA_TIPO_CARTAO,
        COLUNA_TIPO_ATIVIDADE,
        COLUNA_CARGA_HORARIA,
        COLUNA_PERIODO_REFENCIA,
        "Docente substituído",
    ];

    const COLUNAS_MODAL_PLANEJADAS = [
        COLUNA_ATIVIDADE,
        COLUNA_CURSO,
        COLUNA_TIPO_ATIVIDADE,
        COLUNA_CARGA_HORARIA,
        COLUNA_PERIODO_REFENCIA,
    ];

    const COLUNAS_MODAL_ATIVIDADES_REALIZADAS = [
        COLUNA_ATIVIDADE,
        COLUNA_CURSO,
        COLUNA_TIPO_ATIVIDADE,
        COLUNA_CARGA_HORARIA,
        COLUNA_DATA_DE_ALOCACAO,
        "Status",
    ];

    const COLUNAS_MODAL_ATIVIDADES_NAO_REALIZADAS = [
        COLUNA_ATIVIDADE,
        COLUNA_CURSO,
        COLUNA_TIPO_ATIVIDADE,
        COLUNA_CARGA_HORARIA,
        COLUNA_DATA_DE_ALOCACAO,
        "Status",
    ];

    const COLUNAS_MODAL_ATIVIDADES_NAO_ALOCADAS = [
        COLUNA_ATIVIDADE,
        COLUNA_CURSO,
        COLUNA_TIPO_ATIVIDADE,
        COLUNA_CARGA_HORARIA,
        "Status",
        COLUNA_PERIODO_REFENCIA,
    ];

    useEffect(() => {
        function handleClickFora(event: MouseEvent) {
            if (
                refCalendario.current &&
                !refCalendario.current.contains(event.target as Node)
            ) {
                setMostrarCalendario(false);
            }
        }

        if (mostrarCalendario) {
            document.addEventListener("mousedown", handleClickFora);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickFora);
        };
    }, [mostrarCalendario]);

    useEffect(() => {
        const carregarDados = async () => {
            setIsLoading(true);
            setDados(null);
            try {
                const resultado = await buscarExtratoPorMes(
                    mesSelecionado.getMonth(),
                    mesSelecionado.getFullYear()
                );
                setDados(resultado);

                onChangeMes(
                    mesSelecionado.getMonth(),
                    mesSelecionado.getFullYear()
                );
            } catch (error) {
                console.error("Erro ao carregar extrato:", error);
            } finally {
                setIsLoading(false);
            }
        };

        carregarDados();
    }, [mesSelecionado]);

    const toggleSection = (id: SectionKey) => {
        setSectionsOpen((prev) => {
            const updatedSections: Record<SectionKey, boolean> = {
                // Fechar todos os Accordion, exceto o que foi clicado
                naoAlocados: false,
                justificados: false,
                especiais: false,
                substituidas: false,
                letivaRE: false,
            };

            // Se o Accordion clicado não estiver aberto, abra-o
            if (prev[id] === false) {
                console.log("id", prev[id]);

                updatedSections[id] = true;
            }

            return updatedSections;
        });
    };

    const formatarMes = (data: Date) => {
        const meses = [
            "Janeiro",
            "Fevereiro",
            "Março",
            "Abril",
            "Maio",
            "Junho",
            "Julho",
            "Agosto",
            "Setembro",
            "Outubro",
            "Novembro",
            "Dezembro",
        ];
        return `${meses[data.getMonth()]} / ${data.getFullYear()}`;
    };

    const mudarMes = (direcao: number) => {
        if (isLoading) return;
        const novaData = new Date(mesSelecionado);
        novaData.setMonth(novaData.getMonth() + direcao);
        setMesSelecionado(novaData);
    };

    const accordionRef = useRef(null);
    const cardRef = useRef(null);

    const renderSection = (
        id: SectionKey,
        titulo: string,
        children: React.ReactNode,
        total?: any
    ) => (
        <AccordionWrapper onClick={() => toggleSection(id)}>
            <AccordionHeader ref={accordionRef}>
                <span className="titulo">{titulo}</span>
                <span className="valor">{total}</span>
                <div className="icone">
                    {sectionsOpen[id] ? (
                        <ChevronUp size={20} color="rgb(138, 5, 56)" />
                    ) : (
                        <ChevronDown size={20} color="rgb(138, 5, 56)" />
                    )}
                </div>
            </AccordionHeader>
            {sectionsOpen[id] && (
                <AccordionContent>{children}</AccordionContent>
            )}
        </AccordionWrapper>
    );

    const contarCartoes = (lista: any[] | undefined): number =>
        lista?.length ?? 0;

    // Contagem de cartões não alocados
    const naoLetivos = extrato?.cartoesNaoAlocadosDTO?.naoLetivos ?? 0;
    const especiais = contarCartoes(
        extrato?.cartoesNaoAlocadosDTO?.listaEspeciais
    );
    const totalCartoesNaoAlocados = naoLetivos + especiais;

    // Contagem de cartões justificados
    const totalDeferidos = extrato?.cartoesJustificadosDTO?.totalDeferidos ?? 0;
    const totalIndeferidos =
        extrato?.cartoesJustificadosDTO?.totalIndeferidos ?? 0;
    const totalCartoesJustificados = totalDeferidos + totalIndeferidos;
    const extratoCartoesJustificados = processarCartoesJustificados(extrato);

    // Contagem de cartões especiais
    const totalAntecipacao = contarCartoes(
        extrato?.cartoesEspeciaisDTO?.antecipacao
    );
    const totalReposicao = contarCartoes(
        extrato?.cartoesEspeciaisDTO?.reposicao
    );
    const totalSubstituicao = contarCartoes(
        extrato?.cartoesEspeciaisDTO?.substituicao
    );
    const totalCartoesEspeciais =
        totalAntecipacao + totalReposicao + totalSubstituicao;

    // Processamento dos cartões especiais
    const extratoCartoesEspeciais = processarCartoesEspeciais(extrato);

    // Contagem de Letiva Regime Especial
    const totalRealizadas = contarCartoes(
        extrato?.letivaRegimeEspecialDTO?.listaAtividadeRealizada
    );
    const totalNaoRealizadas = contarCartoes(
        extrato?.letivaRegimeEspecialDTO?.listaAtividadeNaoRealizada
    );

    const contarCartoestotalNaoAlocada = (lista: any[] | undefined): number => {
        if (!lista || lista.length === 0) return 0;
        return lista.reduce((total, item) => {
            const cargaHoraria = parseFloat(
                String(item.cargaHoraria).replace(",", ".")
            );
            return total + (Number.isNaN(cargaHoraria) ? 0 : cargaHoraria);
        }, 0);
    };

    const totalNaoAlocada = contarCartoestotalNaoAlocada(
        extrato?.letivaRegimeEspecialDTO?.listaAtividadeNaoAlocada
    );
    const contarCartoestotalPlanejadas =
        totalNaoRealizadas + totalRealizadas + totalNaoAlocada;

    const totalPlanejadas = contarCartoestotalPlanejadas;

    const formatDecimal = (value: string | undefined): string => {
        if (!value || value === "-") return "-";
        return value.replace(".", ",");
    };

    const formatarDadosParaTabela = (
        lista: any[] | undefined
    ): { [key: string]: string }[] => {
        if (!lista) return [];
        return lista.map((item) => {
            return Object.fromEntries(
                Object.entries(item).map(([key, value]) => [key, String(value)]) // Converte os valores para string
            );
        });
    };

    // Dados Modal Deferida e Indeferida

    const formatDate = (dateStr: string | null): string => {
        if (!dateStr) return "";

        const [year, month, day] = dateStr.split("-").map(Number);
        const date = new Date(year, month - 1, day); // Mês começa em 0

        return date.toLocaleDateString("pt-BR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        });
    };

    const mapAtividade = (item: any): Atividade => ({
        atividade: item.atividade ?? "",
        curso: item.curso ?? "",
        tipoCartao: item.tipoCartao ?? "",
        tipoAtividade: item.tipoAtividade ?? "",
        cargaHoraria: item.cargaHoraria ?? "",
        motivoJustificativa: item.motivoJustificativa ?? "",
        dataAlocacao: formatDate(item.dataAlocacao),
        __periodoReferenciaCustom: item.__periodoReferenciaCustom ?? "",
        docenteSubstituto: item.docenteSubstituto ?? "",
        statusCheckin: item.statusCheckin ?? "",
        cartaoVinculado: item.cartaoVinculado ?? "",
        docenteSubstituido: item.docenteSubstituido ?? "",
    });

    const listaDeferidaAbonoLegal: Atividade[] = Array.isArray(
        extrato?.atividadesSubstituidasDTO?.listaDeferidaAbonoLegal
    )
        ? extrato!.atividadesSubstituidasDTO!.listaDeferidaAbonoLegal.map(
              mapAtividade
          )
        : [];

    const listaIndeferidaPendenciaDeJustificativa: Atividade[] = Array.isArray(
        extrato?.atividadesSubstituidasDTO
            ?.listaIndeferidaPendenciaDeJustificativa
    )
        ? extrato!.atividadesSubstituidasDTO!.listaIndeferidaPendenciaDeJustificativa.map(
              mapAtividade
          )
        : [];

    const totalDeferidoModal = Array.isArray(listaDeferidaAbonoLegal)
        ? listaDeferidaAbonoLegal.length
        : 0;

    const totalIndeferidaModal = Array.isArray(
        listaIndeferidaPendenciaDeJustificativa
    )
        ? listaIndeferidaPendenciaDeJustificativa.length
        : 0;
    // if (!dados)
    //     return (
    //         <Wrapper>
    //             <Container>
    //                 <p>Carregando...</p>
    //             </Container>
    //         </Wrapper>
    //     );

    // useEffect(() => {
    //     function handleClickFora(event: MouseEvent) {
    //         // Verifica se o clique foi fora do Accordion Header ou do CardWithArrow
    //         if (
    //             accordionRef.current &&
    //             !accordionRef.current.contains(event.target) &&
    //             cardRef.current &&
    //             !cardRef.current.contains(event.target)
    //         ) {
    //             // Fechar os accordions
    //             setSectionsOpen({
    //                 naoAlocados: false,
    //                 justificados: false,
    //                 especiais: false,
    //                 substituidas: false,
    //                 letivaRE: false,
    //             });
    //         }
    //     }

    //     // Adiciona o evento de clique ao documento
    //     document.addEventListener("mousedown", handleClickFora);

    //     // Cleanup: remove o evento de clique ao desmontar o componente
    //     return () => {
    //         document.removeEventListener("mousedown", handleClickFora);
    //     };
    // }, []);

    const parseDate = (str: any) => {
        if (!str) return new Date(0);
        const inicio = str.split(" a ")[0]; // pega a parte inicial
        return new Date(inicio);
    };

    return (
        <Wrapper>
            <Container>
                <Title>Extrato mensal</Title>
                <Description>
                    As informações exibidas correspondem aos dados já ocorridos
                    dentro do período selecionado.
                </Description>
                <Attention>
                    Atenção! Os dados apresentados são exclusivamente para
                    visualização. Nenhuma ação é necessária.
                </Attention>

                <>
                    <MonthSection>
                        <div className="month-title">Selecione o mês</div>

                        <MonthSelector>
                            <MonthButton
                                onClick={() => mudarMes(-1)}
                                disabled={isLoading}
                            >
                                <ChevronLeft
                                    size={20}
                                    color="rgb(138, 5, 56)"
                                />
                            </MonthButton>

                            <div
                                style={{
                                    position: "relative",
                                    display: "inline-block",
                                }}
                            >
                                <span
                                    role="button"
                                    tabIndex={0}
                                    onClick={() =>
                                        setMostrarCalendario((v) => !v)
                                    }
                                    onKeyDown={(e) => {
                                        if (
                                            e.key === "Enter" ||
                                            e.key === " "
                                        ) {
                                            setMostrarCalendario((v) => !v);
                                        }
                                    }}
                                    style={{
                                        fontSize: "1.1rem",
                                        fontWeight: 500,
                                        minWidth: "100px",
                                        textAlign: "center",
                                        cursor: "pointer",
                                        borderBottom:
                                            "1px dashed rgb(138, 5, 56)",
                                    }}
                                    title="Clique para selecionar um mês"
                                >
                                    {formatarMes(mesSelecionado)}
                                </span>

                                {mostrarCalendario && (
                                    <div
                                        ref={refCalendario}
                                        style={{
                                            position: "absolute",
                                            top: "150%",
                                            left: "50%",
                                            transform: "translateX(-50%)",
                                            zIndex: 9999,
                                            marginTop: "0.25rem",
                                            background: "white",
                                            borderRadius: "8px",
                                            boxShadow:
                                                "0 4px 12px rgba(0,0,0,0.1)",
                                        }}
                                    >
                                        <Calendar
                                            view="month"
                                            dateFormat="mm/yy"
                                            locale="pt-BR"
                                            value={mesSelecionado}
                                            onChange={(e) => {
                                                if (e.value instanceof Date) {
                                                    setMesSelecionado(e.value);
                                                    setMostrarCalendario(false);
                                                }
                                            }}
                                            showButtonBar
                                            inline
                                            readOnlyInput
                                            inputStyle={{ display: "none" }}
                                        />
                                    </div>
                                )}
                            </div>

                            <MonthButton
                                onClick={() => mudarMes(1)}
                                disabled={isLoading}
                            >
                                <ChevronRight
                                    size={20}
                                    color="rgb(138, 5, 56)"
                                />
                            </MonthButton>
                        </MonthSelector>

                        <div />
                    </MonthSection>
                    <CaixaResumo>
                        {renderSection(
                            "naoAlocados",
                            "Cartões não alocados",
                            <>
                                <CardWithArrow
                                    titulo="Não letivo"
                                    valor={naoLetivos.toString()}
                                    onClick={() =>
                                        setShowDialogNaoAlocados(true)
                                    }
                                    ref={cardRef}
                                />
                                <CardWithArrow
                                    titulo="Especiais"
                                    valor={especiais.toString()}
                                    onClick={() =>
                                        setShowDialogNaoAlocadosEspeciais(true)
                                    }
                                />
                            </>,
                            totalCartoesNaoAlocados
                        )}
                        {renderSection(
                            "justificados",
                            "Cartões justificados",
                            <>
                                <CardWithArrow
                                    titulo="Esquecimento / Abono Legal / Abono Administrativo / Dispensado / Feriados / Recessos / Deferidos"
                                    valor={totalDeferidos.toString()}
                                    onClick={() =>
                                        setShowDialogCartoesJustificados(true)
                                    }
                                />
                                <CardWithArrow
                                    titulo="Ausência / Compensados / Indeferidos"
                                    valor={totalIndeferidos.toString()}
                                    onClick={() =>
                                        setShowDialogCartoesJustificadosImpactaFechamento(
                                            true
                                        )
                                    }
                                />
                            </>,
                            totalCartoesJustificados
                        )}
                        {renderSection(
                            "especiais",
                            "Cartões Especiais",
                            <>
                                <CardWithArrow
                                    titulo="Antecipação"
                                    valor={totalAntecipacao.toString()}
                                    onClick={() =>
                                        setShowDialogAntecipacao(true)
                                    }
                                />
                                <CardWithArrow
                                    titulo="Reposição"
                                    valor={totalReposicao.toString()}
                                    onClick={() => setShowDialogReposicao(true)}
                                />
                                <CardWithArrow
                                    titulo="Substituição"
                                    valor={totalSubstituicao.toString()}
                                    onClick={() =>
                                        setShowDialogSubstituicaoCriados(true)
                                    }
                                />
                            </>,
                            totalCartoesEspeciais
                        )}
                        {renderSection(
                            "substituidas",
                            "Minhas atividades substituídas",
                            <>
                                <CardWithArrow
                                    titulo="Deferida / Abono Legal"
                                    valor={totalDeferidoModal.toString()}
                                    onClick={() => setShowDialogDeferida(true)}
                                />
                                <CardWithArrow
                                    titulo="Indeferida / Pendência de justificativa"
                                    valor={totalIndeferidaModal.toString()}
                                    onClick={() =>
                                        setShowDialogIndeferida(true)
                                    }
                                />
                            </>,
                            totalDeferidoModal + totalIndeferidaModal
                        )}
                        {totalPlanejadas > 0 &&
                            renderSection(
                                "letivaRE",
                                "Letiva Regime Especial",
                                <>
                                    {/* <CardWithArrow
                                        titulo="Atividades Planejadas"
                                        valor={totalPlanejadas}
                                        onClick={() =>
                                            setshowDialogPlanejadas(true)
                                        }
                                    /> */}
                                    <CardWithArrow
                                        titulo="Atividades realizadas"
                                        valor={totalRealizadas.toString()}
                                        onClick={() =>
                                            setshowDialogRealizadas(true)
                                        }
                                    />
                                    <CardWithArrow
                                        titulo="Atividades não realizadas"
                                        valor={totalNaoRealizadas.toString()}
                                        onClick={() =>
                                            setshowDialogNaoRealizadas(true)
                                        }
                                    />
                                    <CardWithArrow
                                        titulo="Atividades não alocadas"
                                        valor={formatDecimal(
                                            totalNaoAlocada.toString()
                                        )}
                                        onClick={() =>
                                            setshowDialogNaoAlocadas(true)
                                        }
                                    />
                                </>,
                                formatDecimal(totalPlanejadas.toString())
                            )}
                    </CaixaResumo>
                </>
                {extrato &&
                    extrato?.cartoesNaoAlocadosDTO?.listaNaoLetivos?.length >
                        0 && (
                        <MyPanelModal // Cartão não Alocados
                            open={showDialogNaoAlocados}
                            onClose={() => setShowDialogNaoAlocados(false)}
                            text="Cartões não alocados"
                            title="Atividades Não Letivas e Não Letivas - Matriz Curricular"
                            subtitle="Confira aqui as atividades Não Letivas e Não Letivas - Matriz Curricular que não foram alocadas na agenda."
                            warning="Atenção! Não é possível alocar cartões em períodos passados."
                            columns={COLUNAS_MODAL_NAO_ALOCADOS}
                            rows={
                                extrato?.cartoesNaoAlocadosDTO?.listaNaoLetivos?.map(
                                    (item) =>
                                        Object.fromEntries(Object.entries(item))
                                ) ?? []
                            }
                        />
                    )}

                {extrato &&
                    extrato?.cartoesNaoAlocadosDTO?.listaEspeciais?.length >
                        0 && (
                        <MyPanelModal // Cartão não Alocados - Especiais
                            open={showDialogNaoAlocadosEspeciais}
                            onClose={() =>
                                setShowDialogNaoAlocadosEspeciais(false)
                            }
                            text="Cartões não alocados"
                            title="Antecipação, Reposição e Substituição"
                            subtitle="Confira aqui os cartões de antecipação, reposição e substituição que não foram alocados na Agenda."
                            warning="Atenção! Não é possível alocar cartões em períodos passados."
                            columns={COLUNAS_MODAL_NAO_ALOCADOS_ESPECIAIS}
                            rows={
                                extrato?.cartoesNaoAlocadosDTO?.listaEspeciais
                                    ?.slice() // cria uma cópia para não mutar o original
                                    .sort((a, b) => {
                                        const dateA = new Date(
                                            a.periodoReferenciaInicio.split(
                                                " a "
                                            )[0]
                                        );
                                        const dateB = new Date(
                                            b.periodoReferenciaInicio.split(
                                                " a "
                                            )[0]
                                        );
                                        return (
                                            dateA.getTime() - dateB.getTime()
                                        );
                                    })
                                    .map((item) => ({ ...item })) ?? []
                            }
                        />
                    )}
                {extratoCartoesJustificados &&
                    extratoCartoesJustificados?.cartoesJustificadosDTO
                        ?.listaDeferidos?.length > 0 && (
                        <MyPanelModal // Cartão Justificados - Esquecimento / Abono Legal e Administrado / Feriados/Recessos / Deferidos
                            open={showDialogCartoesJustificados}
                            onClose={() =>
                                setShowDialogCartoesJustificados(false)
                            }
                            text="Cartões justificados"
                            title="Esquecimento / Abono Legal / Abono Administrado / Dispensado / Feriados / Recessos / Deferídos "
                            subtitle="Confira aqui as atividades que foram justificadas"
                            warning="Atenção! Os cartões justificados pelos motivos: Esquecimento, Abono Legal/ Abono Administrativos, Feriados/Recessos, Férias, Licenças e deferidos pela Coordenação/Administração não impactam no fechamento mensal."
                            columns={COLUNAS_MODAL_CARTOES_JUSTIFICADOS}
                            rows={
                                extratoCartoesJustificados?.cartoesJustificadosDTO?.listaDeferidos
                                    ?.slice()
                                    .sort((a, b) => {
                                        const dateA = new Date(
                                            a.periodoReferenciaInicio.split(
                                                " a "
                                            )[0]
                                        );
                                        const dateB = new Date(
                                            b.periodoReferenciaInicio.split(
                                                " a "
                                            )[0]
                                        );
                                        return (
                                            dateA.getTime() - dateB.getTime()
                                        );
                                    })
                                    .map((item) => ({ ...item })) ?? []
                            }
                        />
                    )}
                {extratoCartoesJustificados &&
                    extratoCartoesJustificados?.cartoesJustificadosDTO
                        ?.listaIndeferidos?.length > 0 && (
                        <MyPanelModal // Cartão Justificados - Ausência / Compensados / Indeferidos
                            open={
                                showDialogCartoesJustificadosImpactaFechamento
                            }
                            onClose={() =>
                                setShowDialogCartoesJustificadosImpactaFechamento(
                                    false
                                )
                            }
                            text="Cartões justificados"
                            title="Ausência / Compensados / Indeferidos"
                            subtitle="Confira aqui as atividades que foram justificadas"
                            warning="Atenção! Os cartões justificados pelos motivos: Ausência, Compensados e Indeferido pela Coordenador/Administração impactam no fechamento mensal."
                            columns={
                                COLUNAS_MODAL_CARTOES_JUSTIFICADOS_IMPACTA_FECHAMENTO
                            }
                            rows={
                                extratoCartoesJustificados?.cartoesJustificadosDTO?.listaIndeferidos
                                    ?.slice()
                                    .sort((a, b) => {
                                        const dateA = new Date(
                                            a.periodoReferenciaInicio.split(
                                                " a "
                                            )[0]
                                        );
                                        const dateB = new Date(
                                            b.periodoReferenciaInicio.split(
                                                " a "
                                            )[0]
                                        );
                                        return (
                                            dateA.getTime() - dateB.getTime()
                                        );
                                    })
                                    .map((item) =>
                                        Object.fromEntries(Object.entries(item))
                                    ) ?? []
                            }
                        />
                    )}

                {listaDeferidaAbonoLegal.length > 0 && (
                    <MyPanelModal // Modal Deferida
                        open={showDialogDeferida}
                        onClose={() => setShowDialogDeferida(false)}
                        text="Minhas Atividades Substituídas"
                        title="Deferida / Abono Legal"
                        subtitle="Confira aqui as atividades que foram substituídas por outro docente"
                        warning="Atenção! Atividades que foram substituídas e justificadas."
                        columns={COLUNAS_MODAL_DEFERIDA}
                        rows={
                            listaDeferidaAbonoLegal as Array<
                                Record<string, any>
                            >
                        }
                    />
                )}

                {listaIndeferidaPendenciaDeJustificativa.length > 0 && (
                    <MyPanelModal // Modal Indeferida
                        open={showDialogIndeferida}
                        onClose={() => setShowDialogIndeferida(false)}
                        text="Minhas Atividades Substituídas"
                        title="Indeferida / Pendência de justificativa"
                        subtitle="Confira aqui as atividades indeferidas e ou pendentes de justificativa"
                        warning="Atenção! Atividades que foram substituídas e não foram justificadas."
                        columns={COLUNAS_MODAL_INDEFERIDA}
                        rows={
                            listaIndeferidaPendenciaDeJustificativa as Array<
                                Record<string, any>
                            >
                        }
                    />
                )}

                {extratoCartoesEspeciais &&
                    extratoCartoesEspeciais.cartoesEspeciaisDTO.antecipacao &&
                    extratoCartoesEspeciais.cartoesEspeciaisDTO.antecipacao
                        .length > 0 &&
                    totalAntecipacao > 0 && (
                        <MyPanelModal // Modal para Cartões de Antecipação Criados
                            open={showDialogAntecipacao}
                            onClose={() => setShowDialogAntecipacao(false)}
                            text="Cartões especiais"
                            title="Cartões do tipo Antecipação criados"
                            subtitle="Confira aqui as atividades que foram antecipadas"
                            warning="Atenção: Atividade antecipada deve ser vinculada a atividade de origem."
                            columns={COLUNAS_MODAL_ANTECIPACAO}
                            rows={formatarDadosParaTabela(
                                extratoCartoesEspeciais?.cartoesEspeciaisDTO
                                    ?.antecipacao
                            )}
                        />
                    )}

                {extratoCartoesEspeciais &&
                    extratoCartoesEspeciais.cartoesEspeciaisDTO?.reposicao
                        ?.length > 0 &&
                    totalReposicao > 0 && (
                        <MyPanelModal // Modal para Cartões de Reposição Criados
                            open={showDialogReposicao}
                            onClose={() => setShowDialogReposicao(false)}
                            text="Cartões especiais"
                            title="Cartões do tipo Reposição criados"
                            subtitle="Confira aqui atividades que foram respostas"
                            columns={COLUNAS_MODAL_REPOSICAO}
                            rows={formatarDadosParaTabela(
                                extratoCartoesEspeciais?.cartoesEspeciaisDTO
                                    ?.reposicao
                            )}
                        />
                    )}

                {extratoCartoesEspeciais &&
                    extratoCartoesEspeciais.cartoesEspeciaisDTO?.substituicao
                        ?.length > 0 &&
                    totalReposicao > 0 && (
                        <MyPanelModal // Modal para Cartões de Substituição Criados
                            open={showDialogSubstituicaoCriados}
                            onClose={() =>
                                setShowDialogSubstituicaoCriados(false)
                            }
                            text="Cartões especiais"
                            title="Cartões do tipo Substituição criados"
                            subtitle="Confira aqui as atividades que você substituiu"
                            columns={COLUNAS_MODAL_SUBSTITUICAO_CRIADOS}
                            rows={formatarDadosParaTabela(
                                extratoCartoesEspeciais?.cartoesEspeciaisDTO
                                    ?.substituicao
                            )}
                        />
                    )}

                {extratoCartoesEspeciais &&
                    extratoCartoesEspeciais.letivaRegimeEspecialDTO
                        .listaAtividadePlanejada?.length > 0 && (
                        <MyPanelModal // Modal Planejadas
                            open={showDialogPlanejadas}
                            onClose={() => setshowDialogPlanejadas(false)}
                            text="Letiva Regime Especial"
                            title="Atividades planejadas"
                            subtitle="Confira aqui as atividades indeferidas e ou pendentes de justificativa"
                            columns={COLUNAS_MODAL_PLANEJADAS}
                            rows={formatarDadosParaTabela(
                                extrato?.letivaRegimeEspecialDTO
                                    ?.listaAtividadePlanejada
                            )}
                        />
                    )}

                {extratoCartoesEspeciais &&
                    extratoCartoesEspeciais.letivaRegimeEspecialDTO
                        .listaAtividadeRealizada?.length > 0 && (
                        <MyPanelModal // Modal Realizadas
                            open={showDialogRealizadas}
                            onClose={() => setshowDialogRealizadas(false)}
                            text="Letiva Regime Especial"
                            title="Atividades realizadas"
                            subtitle="Confira aqui as atividades Letiva Regime Especial realizadas por você"
                            columns={COLUNAS_MODAL_ATIVIDADES_REALIZADAS}
                            rows={formatarDadosParaTabela(
                                extrato?.letivaRegimeEspecialDTO
                                    ?.listaAtividadeRealizada
                            )}
                        />
                    )}

                {extratoCartoesEspeciais &&
                    extratoCartoesEspeciais.letivaRegimeEspecialDTO
                        .listaAtividadeNaoRealizada?.length > 0 && (
                        <MyPanelModal // Modal Não Realizadas
                            open={showDialogNaoRealizadas}
                            onClose={() => setshowDialogNaoRealizadas(false)}
                            text="Letiva Regime Especial"
                            title="Atividades não realizadas"
                            subtitle="Confira aqui as atividades Letiva Regime Especial não realizadas"
                            columns={COLUNAS_MODAL_ATIVIDADES_NAO_REALIZADAS}
                            rows={formatarDadosParaTabela(
                                extrato?.letivaRegimeEspecialDTO
                                    ?.listaAtividadeNaoRealizada
                            )}
                        />
                    )}

                {extratoCartoesEspeciais &&
                    extratoCartoesEspeciais.letivaRegimeEspecialDTO
                        .listaAtividadeNaoAlocada?.length > 0 && (
                        <MyPanelModal // Modal Não Alocadas
                            open={showDialogNaoAlocadas}
                            onClose={() => setshowDialogNaoAlocadas(false)}
                            text="Letiva Regime Especial"
                            title="Atividades não alocadas"
                            subtitle="Confira aqui as atividades Letiva Regime Especial não alocadas na agenda"
                            columns={COLUNAS_MODAL_ATIVIDADES_NAO_ALOCADAS}
                            rows={formatarDadosParaTabela(
                                extrato?.letivaRegimeEspecialDTO
                                    ?.listaAtividadeNaoAlocada
                            )}
                        />
                    )}
            </Container>
        </Wrapper>
    );
};

export default ExtratoMensal;
